import { ChevronRight, Headphones, Heart, MessageSquareText } from "lucide-react";
import { Link } from "react-router";
import { useBackState } from "@/book/components/back";
import type { Chapter, ChapterRef, Exercise } from "@/shared/api/book";
import { ATTITUDE_LABEL, countLine, formatDate, MARK_ICON, MARK_LABEL } from "@/shared/lib/labels";
import { Chip } from "@/shared/ui/chip";

/** Fejezet-sor (design-rendszer 6.): fejezetszám · cím + számlálók · chevron. */
export function ChapterRow({ chapter }: { chapter: Chapter }) {
  const counts = countLine([
    [chapter.counts.exercises, "gyakorlat"],
    [chapter.counts.glossary, "fogalom"],
    [chapter.counts.experiences, "tapasztalat"],
    [chapter.counts.errata, "javítás"],
  ]);
  return (
    <Link
      to={`/app/book/fejezetek/${chapter.id}`}
      className="flex items-center gap-3.5 border-t border-line py-3 hover:bg-paper"
    >
      <span className="w-10 shrink-0 text-[26px] font-extrabold leading-none tracking-[-0.02em] text-gold">
        {chapter.number}
      </span>
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="text-[15px] font-semibold">{chapter.title}</span>
        <span className="text-[12px] text-muted">{counts || "még nincs tartalma"}</span>
      </span>
      <ChevronRight size={20} className="shrink-0 text-muted" />
    </Link>
  );
}

/** Fejezet-lista alfejezetekkel: a gyerekek fél behúzással, a szülő alatt.
 *  Ugyanez a szerkezet a kezdőlapon és a fejezet-listában (design-rendszer 7.). */
export function ChapterList({ chapters }: { chapters: Chapter[] }) {
  return (
    <div className="flex flex-col">
      {chapters.map((chapter) => (
        <div key={chapter.id}>
          <ChapterRow chapter={chapter} />
          {chapter.children.length > 0 && (
            <div className="pl-[27px]">
              {chapter.children.map((child) => (
                <ChapterRow key={child.id} chapter={child} />
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

export function ExerciseChips({
  exercise,
  hideChapter = false,
}: {
  exercise: Exercise;
  /** A fejezet-oldalon fölösleges kiírni, melyik fejezethez tartozik. */
  hideChapter?: boolean;
}) {
  return (
    <span className="flex flex-wrap items-center gap-1.5">
      {!hideChapter &&
        exercise.chapters.map((c) => <Chip key={c.id}>{c.number}. fejezet</Chip>)}
      {exercise.attitude && <Chip>{ATTITUDE_LABEL[exercise.attitude]}</Chip>}
      {/* A gyakorlat fajtáját (`kind`) és az „AI-val" jelölést (`needs_ai`) tároljuk, de nem
          mutatjuk: a meditációt a hang-ikon jelzi, az AI-funkciók a 3. fázisban jönnek. */}
    </span>
  );
}

/** A gyakorlat-soron megjelenő ikonok: van-e hozzá hang, és mit jelöltem róla.
 *  Ikon-önmagában áll, ezért mindegyik `title`-t (és képernyőolvasónak szöveget) kap. */
export function ExerciseMarkIcons({ exercise }: { exercise: Exercise }) {
  const MarkIcon = exercise.mark.status ? MARK_ICON[exercise.mark.status] : null;
  return (
    <>
      {exercise.audio_url && (
        <Headphones
          size={14}
          className="shrink-0 text-muted"
          aria-label="Van hozzá hanganyag"
        />
      )}
      {MarkIcon && exercise.mark.status && (
        <MarkIcon
          size={14}
          className="shrink-0 text-ink-soft"
          aria-label={MARK_LABEL[exercise.mark.status]}
        />
      )}
      {exercise.mark.is_favorite && (
        <Heart
          size={14}
          className="shrink-0 text-gold"
          fill="currentColor"
          aria-label="Kedvenc"
        />
      )}
    </>
  );
}

/** Gyakorlat lista-sor: fejezet · cím + ikonok + hozzáállás. */
export function ExerciseRow({
  exercise,
  hideChapter = false,
}: {
  exercise: Exercise;
  hideChapter?: boolean;
}) {
  const back = useBackState();
  return (
    <Link
      to={`/app/book/gyakorlatok/${exercise.id}`}
      state={back}
      className="flex items-center gap-3 border-t border-line py-3 hover:bg-paper"
    >
      <span className="flex min-w-0 flex-1 flex-col gap-1">
        {/* A fejezet halványan, egy sorban a cím fölött — nem versenyez a címmel. */}
        {!hideChapter && exercise.chapters.length > 0 && (
          <span className="text-[12px] leading-tight text-muted">
            {exercise.chapters.map((c) => `${c.number}. fejezet`).join(" · ")}
          </span>
        )}
        <span className="flex items-center gap-2">
          <span className="text-[15px] font-semibold leading-snug">{exercise.title}</span>
          <ExerciseMarkIcons exercise={exercise} />
        </span>
        <ExerciseChips exercise={exercise} hideChapter />
      </span>
      <ChevronRight size={20} className="shrink-0 text-muted" />
    </Link>
  );
}

export function ChapterChips({ chapters }: { chapters: ChapterRef[] }) {
  return (
    <>
      {chapters.map((c) => (
        <Chip key={c.id}>{c.number}. fejezet</Chip>
      ))}
    </>
  );
}

export function MetaLine({
  author,
  created_at,
  replies,
}: {
  author: string;
  created_at: string;
  replies?: number;
}) {
  return (
    <p className="flex items-center gap-2 text-[12px] text-muted">
      <span className="font-semibold text-ink-soft">{author}</span>
      <span>{formatDate(created_at)}</span>
      {replies ? (
        <span className="flex items-center gap-1">
          <MessageSquareText size={12} />
          {replies}
        </span>
      ) : null}
    </p>
  );
}
